import React, { useState, useEffect } from 'react';
import '@google/model-viewer';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { Card } from '@/components/ui/card';
import { calculateMeshMetrics, validateMesh } from '@/utils/meshCalculator';

export const ModelViewerComponent = ({ file, onMetricsCalculated }) => {
  const [glbUrl, setGlbUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) {
      setGlbUrl(null);
      setMetrics(null);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        console.log('Loading STL file...');
        const loader = new STLLoader();
        const geometry = loader.parse(event.target.result);
        
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        
        console.log('Calculating metrics...');
        const meshMetrics = calculateMeshMetrics(geometry);
        const validation = validateMesh(meshMetrics);
        const fullMetrics = { ...meshMetrics, ...validation };
        
        setMetrics(fullMetrics);
        onMetricsCalculated(fullMetrics);

        // Convert STL geometry to GLB format
        console.log('Converting to GLB...');
        const material = new THREE.MeshStandardMaterial({
          color: 0xff6b35,
          roughness: 0.5,
          metalness: 0.3
        });
        
        const mesh = new THREE.Mesh(geometry, material);
        
        // Center the mesh
        const bbox = geometry.boundingBox;
        const center = new THREE.Vector3();
        bbox.getCenter(center);
        mesh.position.sub(center);
        
        // Create scene with mesh
        const scene = new THREE.Scene();
        scene.add(mesh);
        
        // Export to GLB
        const exporter = new GLTFExporter();
        exporter.parse(
          scene,
          (result) => {
            console.log('GLB export successful');
            const blob = new Blob([result], { type: 'model/gltf-binary' });
            const url = URL.createObjectURL(blob);
            setGlbUrl(url);
            setLoading(false);
          },
          (error) => {
            console.error('GLB export error:', error);
            setError('Failed to convert model');
            setLoading(false);
          },
          { binary: true }
        );
        
      } catch (err) {
        console.error('Error processing file:', err);
        setError('Failed to process 3D model');
        setLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read file');
      setLoading(false);
    };
    
    reader.readAsArrayBuffer(file);

    // Cleanup
    return () => {
      if (glbUrl) {
        URL.revokeObjectURL(glbUrl);
      }
    };
  }, [file, onMetricsCalculated]);

  if (!file) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400">
        <div className="p-6 rounded-2xl bg-gray-50 mb-4 border border-gray-200">
          <svg className="h-16 w-16 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
        <p className="text-lg font-medium text-gray-700">Upload a 3D file to visualize</p>
        <p className="text-sm text-gray-500 mt-1">STL, 3MF formats supported</p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full bg-gray-900">
      {glbUrl && !loading && (
        <model-viewer
          src={glbUrl}
          alt="3D Model"
          camera-controls
          auto-rotate
          shadow-intensity="1"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: '#2a2a2a'
          }}
          exposure="1"
          shadow-softness="0.5"
        />
      )}

      {/* Model Stats Overlay */}
      {metrics && (
        <Card className="absolute bottom-4 left-4 p-3 bg-white/95 backdrop-blur-sm border-purple-200 shadow-lg z-10">
          <div className="text-xs font-semibold text-purple-700 mb-2">Model Stats</div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Volume:</span>
              <span className="font-mono font-semibold text-gray-800">
                {metrics.volume_cm3} cm³
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Surface:</span>
              <span className="font-mono font-semibold text-gray-800">
                {metrics.surface_cm2} cm²
              </span>
            </div>
          </div>
        </Card>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm z-20">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            <div className="text-white font-medium">Converting model...</div>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 backdrop-blur-sm z-20">
          <Card className="bg-red-50 border-red-200 p-4 max-w-md">
            <div className="text-red-700">{error}</div>
          </Card>
        </div>
      )}
    </div>
  );
};
